import { EVMData } from '@/types/evm';

/**
 * Forecast scenario types for EVM calculations.
 */
export type ForecastMethod = 'cpi_based' | 'spi_based' | 'composite';

/**
 * Extended EVM metrics including multiple forecast scenarios.
 */
export interface AdvancedEVMMetrics {
  // Basic metrics
  scheduleVariance: number | null;
  costVariance: number | null;
  spi: number | null;
  cpi: number | null;
  tcpi: number | null;

  // Extended metrics
  percentComplete: number | null;
  percentScheduled: number | null;
  percentSpent: number | null;

  // Multiple forecast scenarios
  forecasts: {
    cpiBased: ForecastScenario;
    spiBased: ForecastScenario;
    composite: ForecastScenario;
  };

  // Project health assessment
  healthStatus: 'on_track' | 'at_risk' | 'critical';
  healthIndicators: HealthIndicator[];
}

/**
 * Forecast scenario with EAC and ETC calculations.
 */
export interface ForecastScenario {
  method: ForecastMethod;
  label: string;
  eac: number | null;
  etc: number | null;
  vac: number | null; // Variance at Completion (BAC - EAC)
  description: string;
}

/**
 * Health indicator for project status assessment.
 */
export interface HealthIndicator {
  metric: string;
  value: number | null;
  status: 'good' | 'warning' | 'critical';
  message: string;
}

/**
 * S-Curve data point for visualization.
 */
export interface SCurveDataPoint {
  period: number;
  date: Date;
  plannedValue: number;
  earnedValue: number;
  actualCost: number;
  label: string;
}

/**
 * Calculates advanced EVM metrics including multiple forecast scenarios.
 * Returns null for metrics that cannot be calculated due to invalid inputs.
 */
export function calculateAdvancedMetrics(data: EVMData): AdvancedEVMMetrics {
  const bac = data.bac ?? 0;
  const ac = data.ac ?? 0;
  const pv = data.pv ?? 0;
  const ev = data.ev ?? 0;

  // Basic variance calculations
  const scheduleVariance = ev - pv;
  const costVariance = ev - ac;

  // Performance indices (return null for invalid divisions)
  const spi = pv !== 0 ? ev / pv : null;
  const cpi = ac !== 0 ? ev / ac : null;

  // TCPI calculation
  const remainingBudget = bac - ac;
  const remainingWork = bac - ev;
  const tcpi = remainingBudget !== 0 ? remainingWork / remainingBudget : null;

  // Percentage metrics
  const percentComplete = bac !== 0 ? (ev / bac) * 100 : null;
  const percentScheduled = bac !== 0 ? (pv / bac) * 100 : null;
  const percentSpent = bac !== 0 ? (ac / bac) * 100 : null;

  // Forecast scenarios
  const forecasts = {
    cpiBased: calculateCPIBasedForecast(bac, ac, cpi),
    spiBased: calculateSPIBasedForecast(bac, ac, ev, spi),
    composite: calculateCompositeForecast(bac, ac, ev, cpi, spi),
  };

  // Health assessment
  const healthIndicators = assessProjectHealth(spi, cpi, tcpi, percentComplete);
  const healthStatus = determineOverallHealth(healthIndicators);

  return {
    scheduleVariance,
    costVariance,
    spi,
    cpi,
    tcpi,
    percentComplete,
    percentScheduled,
    percentSpent,
    forecasts,
    healthStatus,
    healthIndicators,
  };
}

/**
 * CPI-Based Forecast: Assumes future cost performance will match current CPI.
 * EAC = BAC / CPI
 */
function calculateCPIBasedForecast(
  bac: number,
  ac: number,
  cpi: number | null
): ForecastScenario {
  if (cpi === null || cpi === 0) {
    return {
      method: 'cpi_based',
      label: 'CPI-Based (Typical)',
      eac: null,
      etc: null,
      vac: null,
      description: 'Cannot calculate: CPI is zero or undefined',
    };
  }

  const eac = bac / cpi;
  const etc = Math.max(eac - ac, 0);
  const vac = bac - eac;

  return {
    method: 'cpi_based',
    label: 'CPI-Based (Typical)',
    eac,
    etc,
    vac,
    description: 'Assumes future cost performance matches current CPI',
  };
}

/**
 * SPI-Based Forecast: Considers schedule performance impact on costs.
 * EAC = AC + (BAC - EV) / SPI
 */
function calculateSPIBasedForecast(
  bac: number,
  ac: number,
  ev: number,
  spi: number | null
): ForecastScenario {
  if (spi === null || spi === 0) {
    return {
      method: 'spi_based',
      label: 'SPI-Based (Schedule-Driven)',
      eac: null,
      etc: null,
      vac: null,
      description: 'Cannot calculate: SPI is zero or undefined',
    };
  }

  const remainingWork = bac - ev;
  const eac = ac + remainingWork / spi;
  const etc = Math.max(eac - ac, 0);
  const vac = bac - eac;

  return {
    method: 'spi_based',
    label: 'SPI-Based (Schedule-Driven)',
    eac,
    etc,
    vac,
    description: 'Considers schedule performance impact on remaining work',
  };
}

/**
 * Composite Forecast: Combines CPI and SPI for a balanced estimate.
 * EAC = AC + (BAC - EV) / (CPI * SPI)
 */
function calculateCompositeForecast(
  bac: number,
  ac: number,
  ev: number,
  cpi: number | null,
  spi: number | null
): ForecastScenario {
  if (cpi === null || spi === null || cpi === 0 || spi === 0) {
    return {
      method: 'composite',
      label: 'Composite (Balanced)',
      eac: null,
      etc: null,
      vac: null,
      description: 'Cannot calculate: CPI or SPI is zero or undefined',
    };
  }

  const remainingWork = bac - ev;
  const compositeIndex = cpi * spi;
  const eac = ac + remainingWork / compositeIndex;
  const etc = Math.max(eac - ac, 0);
  const vac = bac - eac;

  return {
    method: 'composite',
    label: 'Composite (Balanced)',
    eac,
    etc,
    vac,
    description: 'Balanced estimate using both cost and schedule performance',
  };
}

/**
 * Assesses project health based on key EVM metrics.
 */
function assessProjectHealth(
  spi: number | null,
  cpi: number | null,
  tcpi: number | null,
  percentComplete: number | null
): HealthIndicator[] {
  const indicators: HealthIndicator[] = [];

  // SPI Assessment
  if (spi !== null) {
    let status: 'good' | 'warning' | 'critical';
    let message: string;

    if (spi >= 0.95) {
      status = 'good';
      message = 'On or ahead of schedule';
    } else if (spi >= 0.85) {
      status = 'warning';
      message = 'Slightly behind schedule';
    } else {
      status = 'critical';
      message = 'Significantly behind schedule';
    }

    indicators.push({
      metric: 'Schedule Performance (SPI)',
      value: spi,
      status,
      message,
    });
  }

  // CPI Assessment
  if (cpi !== null) {
    let status: 'good' | 'warning' | 'critical';
    let message: string;

    if (cpi >= 0.95) {
      status = 'good';
      message = 'On or under budget';
    } else if (cpi >= 0.85) {
      status = 'warning';
      message = 'Slightly over budget';
    } else {
      status = 'critical';
      message = 'Significantly over budget';
    }

    indicators.push({
      metric: 'Cost Performance (CPI)',
      value: cpi,
      status,
      message,
    });
  }

  // TCPI Assessment
  if (tcpi !== null) {
    let status: 'good' | 'warning' | 'critical';
    let message: string;

    if (tcpi <= 1.05) {
      status = 'good';
      message = 'Achievable efficiency target';
    } else if (tcpi <= 1.2) {
      status = 'warning';
      message = 'Challenging efficiency target';
    } else {
      status = 'critical';
      message = 'Very difficult to achieve on budget';
    }

    indicators.push({
      metric: 'Required Efficiency (TCPI)',
      value: tcpi,
      status,
      message,
    });
  }

  // Completion Assessment
  if (percentComplete !== null) {
    indicators.push({
      metric: 'Completion',
      value: percentComplete,
      status: 'good',
      message: `${percentComplete.toFixed(1)}% complete`,
    });
  }

  return indicators;
}

/**
 * Determines overall project health status based on indicators.
 */
function determineOverallHealth(
  indicators: HealthIndicator[]
): 'on_track' | 'at_risk' | 'critical' {
  const criticalCount = indicators.filter((i) => i.status === 'critical').length;
  const warningCount = indicators.filter((i) => i.status === 'warning').length;

  if (criticalCount > 0) {
    return 'critical';
  }
  if (warningCount > 0) {
    return 'at_risk';
  }
  return 'on_track';
}

/**
 * Generates S-Curve data points for visualization.
 * Creates interpolated data based on project dates and current values.
 */
export function generateSCurveData(
  data: EVMData,
  periodCount: number = 12
): SCurveDataPoint[] {
  const { startDate, endDate, bac, ev, ac } = data;

  if (!startDate || !endDate || !bac) {
    return [];
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalDuration = end.getTime() - start.getTime();
  const now = new Date();

  // Calculate current period (0 to periodCount)
  const elapsedTime = Math.min(now.getTime() - start.getTime(), totalDuration);
  const currentPeriodIndex = Math.floor((elapsedTime / totalDuration) * periodCount);

  const dataPoints: SCurveDataPoint[] = [];

  for (let i = 0; i <= periodCount; i++) {
    const periodFraction = i / periodCount;
    const periodDate = new Date(start.getTime() + totalDuration * periodFraction);

    // S-Curve formula for planned value (uses sigmoid-like distribution)
    const sCurveFactor = calculateSCurveFactor(periodFraction);
    const plannedValueAtPeriod = bac * sCurveFactor;

    // For past periods, interpolate earned value and actual cost
    let earnedValueAtPeriod = 0;
    let actualCostAtPeriod = 0;

    if (i <= currentPeriodIndex && ev !== null && ac !== null) {
      const progressFraction = i / Math.max(currentPeriodIndex, 1);
      earnedValueAtPeriod = ev * progressFraction;
      actualCostAtPeriod = ac * progressFraction;
    }

    dataPoints.push({
      period: i,
      date: periodDate,
      plannedValue: plannedValueAtPeriod,
      earnedValue: earnedValueAtPeriod,
      actualCost: actualCostAtPeriod,
      label: `Period ${i}`,
    });
  }

  return dataPoints;
}

/**
 * Calculates S-Curve factor using a sigmoid-like function.
 * This creates the characteristic S-shape where work accelerates
 * in the middle of the project and tapers off at the ends.
 */
function calculateSCurveFactor(fraction: number): number {
  // Using a simplified S-curve formula
  // Steeper in the middle, flatter at the ends
  const k = 10; // Steepness factor
  const midpoint = 0.5;

  // Logistic function normalized to 0-1 range
  const raw = 1 / (1 + Math.exp(-k * (fraction - midpoint)));

  // Normalize to ensure we get 0 at start and 1 at end
  const atStart = 1 / (1 + Math.exp(-k * (0 - midpoint)));
  const atEnd = 1 / (1 + Math.exp(-k * (1 - midpoint)));

  return (raw - atStart) / (atEnd - atStart);
}

/**
 * Formats a number as currency with proper null handling.
 */
export function formatCurrencyValue(value: number | null, fallback: string = 'N/A'): string {
  if (value === null || isNaN(value)) {
    return fallback;
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Formats a ratio/index value with proper null handling.
 */
export function formatIndexValue(value: number | null, fallback: string = 'N/A'): string {
  if (value === null || isNaN(value)) {
    return fallback;
  }
  return value.toFixed(2);
}

/**
 * Formats a percentage value with proper null handling.
 */
export function formatPercentageValue(value: number | null, fallback: string = 'N/A'): string {
  if (value === null || isNaN(value)) {
    return fallback;
  }
  return `${value.toFixed(1)}%`;
}
